import { createContext, useContext, useEffect, useRef, useState } from "react";
import { MotionConfig, useMotionValue } from "framer-motion";
import { Sparkles } from "lucide-react";

const MotionContext = createContext({
  reduced: false,
  direction: "down",
  toggle: () => {},
});
export function MotionPreferences({ children }) {
  // Each visit starts with motion on; the toggle applies to the current visit.
  const [reduced, setReduced] = useState(false);
  const [direction, setDirection] = useState("down");
  const lastScroll = useRef(0);
  useEffect(() => {
    lastScroll.current = Math.max(0, window.scrollY);
    const onScroll = () => {
      const next = Math.max(0, window.scrollY);
      const delta = next - lastScroll.current;
      if (Math.abs(delta) < 2) return;
      lastScroll.current = next;
      setDirection(delta > 0 ? "down" : "up");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.motion = reduced ? "reduced" : "full";
    document.documentElement.dataset.scrollDirection = direction;
    return () => {
      delete document.documentElement.dataset.motion;
      delete document.documentElement.dataset.scrollDirection;
    };
  }, [reduced, direction]);
  return (
    <MotionContext.Provider
      value={{
        reduced,
        direction,
        toggle: () => setReduced((value) => !value),
      }}
    >
      <MotionConfig reducedMotion={reduced ? "always" : "never"}>
        {children}
      </MotionConfig>
    </MotionContext.Provider>
  );
}
export function useSiteReducedMotion() {
  return useContext(MotionContext).reduced;
}
export function useScrollDirection() {
  return useContext(MotionContext).direction;
}

// Scroll-linked artwork retains its furthest position instead of reversing.
export function useForwardProgress(source) {
  const direction = useScrollDirection();
  const value = useMotionValue(source.get());
  useEffect(() => {
    const update = (next) => {
      if (direction === "down") value.set(Math.max(value.get(), next));
    };
    update(source.get());
    return source.on("change", update);
  }, [source, value, direction]);
  return value;
}
export function MotionToggle() {
  const { reduced, toggle } = useContext(MotionContext);
  return (
    <button
      type="button"
      className="motion-toggle"
      onClick={toggle}
      aria-label={reduced ? "Enable animations" : "Reduce animations"}
      aria-pressed={!reduced}
      title={reduced ? "Enable animations" : "Reduce animations"}
    >
      <Sparkles size={17} />
      <span>Motion {reduced ? "off" : "on"}</span>
    </button>
  );
}
